import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '@/shared/api/errors';
import { sessionEndKey, sessionQueryOptions } from '@/shared/session/currentUser';

import { authApi, sessionQueryKey } from './api';
import {
  isSecondFactorPending,
  type LoginPayload,
  type LoginResult,
  type PasswordChange,
  type User,
} from './types';

/**
 * Current session, with the options every reader of it shares (`sessionQueryOptions`:
 * the request, no retry on a 401, a minute fresh).
 */
export function useSession() {
  return useQuery(sessionQueryOptions);
}

/**
 * The password. With a code still owed (`SecondFactorPending`, contract v10.45) the session
 * is **not** open: the cookie only opens the second factor endpoints and `/api/auth/me`
 * still says 401, so nothing goes into the session cache until the code is right. Either
 * way the reason the API closed the last session is no longer news.
 */
export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation<LoginResult, ApiError, LoginPayload>({
    mutationFn: authApi.login,
    onSuccess: (result) => {
      queryClient.removeQueries({ queryKey: sessionEndKey });
      if (isSecondFactorPending(result)) return;
      queryClient.setQueryData<User>(sessionQueryKey, result);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: authApi.logout,
    onSettled: () => {
      // Whatever the server answered, this browser no longer holds a session.
      queryClient.clear();
    },
  });
}

/** Change one's own password. The session object is patched by the caller. */
export function useChangePassword() {
  return useMutation<User, ApiError, PasswordChange>({ mutationFn: authApi.changePassword });
}

/**
 * Save one's own signature (contract v10.41). The answer is the session, which replaces
 * the cached one: the next form of the mail manager is signed with it.
 */
export function useChangeSignature() {
  const queryClient = useQueryClient();
  return useMutation<User, ApiError, string>({
    mutationFn: authApi.changeSignature,
    onSuccess: (user) => {
      queryClient.setQueryData(sessionQueryKey, user);
    },
  });
}
